import type { UseQueryOptions } from 'react-query';
import { useQuery } from 'react-query';

import type { APIError } from 'utils/errors';

import { getRequest } from './apiRequests';

const apiUrl = '/api/csrf';

type CSRFTokenResponse = {
  csrf: string;
};

const getCSRFToken = () => getRequest<null, CSRFTokenResponse>(apiUrl);

/**
 * CSRF value from the host shell (window.ciq). It matches the Django session token (CSRF_USE_SESSIONS) and
 * does not rotate on a short client timer. Use GET /api/csrf only when this is empty or when recovering from
 * 403 `csrf_failure` (see fetchMutateWithCsrfRetry + setQueryData).
 */
const getPageCsrfBootstrap = (): string =>
  typeof window !== 'undefined' ? ((window as { ciq?: { csrfToken?: string } }).ciq?.csrfToken ?? '') : '';

const useGetCSRFToken = (options: UseQueryOptions<CSRFTokenResponse, APIError> = {}) => {
  const bootstrap = getPageCsrfBootstrap();
  const hasBootstrap = Boolean(bootstrap);

  return {
    query: useQuery<CSRFTokenResponse, APIError>('csrf', getCSRFToken, {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      initialData: hasBootstrap ? { csrf: bootstrap } : undefined,
      enabled: !hasBootstrap,
      ...options,
    }),
  };
};

export const CSRFService = {
  apiUrl,
  getPageCsrfBootstrap,
  useGetCSRFToken,
};
